import { useState } from 'react';
import { IRecomendVideo, VideoCard } from '../model/home';
import { mySubjectColors, subjectColors } from '../consts/subject';
import { formatTimeAgo } from '../utils/DateUtils';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import PlayBtn from './ui/PlayBtn';
import { formatDuration } from '../utils/VideoUtils';
import { Play } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

const RecommendedClipsGrid = (props: {
  videos: VideoCard[];
  onVideoClick: (videoId: string) => void;
}) => {
  const recommendedVideosList: IRecomendVideo[] = props.videos.map((rv) => ({
    id: rv.video_id,
    question: rv.title,
    subject: rv.subject,
    views: '1.2k',
    likes: 200,
    cover_img: rv.cover_img,
    video_url: rv.video_url,
    createdAt: new Date(rv.create_ts * 1000),
    video_duration: rv.video_duration,
  }));
  const [visibleCount, setVisibleCount] = useState(8);

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 8, recommendedVideosList.length));
  };

  // const visibleVideos:IRecomendVideo[] = recommendedVideosList.slice(0, visibleCount);
  const hasMore = false;

  return (
    <>
      <div className="pb-6">
        <div className="flex flex-wrap">
          {(recommendedVideosList || []).map((video, index) => {
            const videoSubject = video.subject || 'Others';
            const subjectColor = subjectColors[videoSubject]
              ? subjectColors[videoSubject]
              : subjectColors['Others'];

            return (
              <Card
                key={video.id}
                onClick={() => props.onVideoClick(video.id)}
                className="w-[280px] m-2 overflow-hidden bg-[#fdfbf6] transition-all duration-300 cursor-pointer hover:scale-105 rounded-3xl animate-in fade-in slide-in-from-bottom-4"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Thumbnail */}
                <div
                  className="relative aspect-video flex items-center justify-center overflow-hidden"
                  style={{
                    // background:
                    //   'linear-gradient(135deg, rgba(255, 231, 92, 0.1) 10%, rgba(255, 231, 92, 0.05) 95%)',
                    background: `url(${video.cover_img || 'https://cdn.jumpstar.app/solve/ai_gen/20251201/9f2c75b4-6e7d-4380-9ca5-c88d066b5c1f.png'}) center center / 100% no-repeat`,
                    borderRadius: '22px 22px 0px 0px',
                  }}
                >
                  {/* Play button with white background circle */}
                  <PlayBtn />

                  {/* Duration */}
                  <div className="absolute bottom-2 right-2">
                    <div className="bg-black/30 text-white text-xs px-2 py-0.5 rounded">
                      {formatDuration(Math.floor(video.video_duration))}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3 pt-0.5 flex flex-col h-[140px]">
                  {/* Subject Badge and Creation Time */}
                  <div className="flex items-center justify-between mb-2">
                    <Badge
                      className={`${subjectColor.bg} ${subjectColor.text} rounded-full px-3 py-0.5 border border-black/5`}
                    >
                      {videoSubject}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatTimeAgo(video.createdAt)}
                    </span>
                  </div>

                  {/* Title - always takes 2 lines space */}
                  <h4 className="mb-2 line-clamp-2 min-h-12">
                    {video.question}
                  </h4>

                  {/* Spacer to push button to bottom */}
                  <div className="grow"></div>

                  {/* Ready to play button */}
                  <Button className="w-full rounded-2xl flex items-center justify-start gap-2 bg-[#302f2b] hover:bg-[#302f2b]/90 text-[#ffe75c] h-9">
                    <Play className="w-4 h-4" />
                    <span>Ready to play</span>
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {hasMore && (
        <div className="flex justify-center mt-8">
          <Button
            onClick={handleLoadMore}
            variant="outline"
            className="px-8 py-6 rounded-full bg-white hover:bg-primary/10 border-2 border-black/10 hover:border-primary transition-all hover:scale-105"
          >
            Load More
          </Button>
        </div>
      )}
    </>
  );
};

export default RecommendedClipsGrid;
